import { ViewIds } from '@ardoq/api-types';
import { Icon, IconName, IconSize, SvgImage } from '@ardoq/icons';
import styled from 'styled-components';
import { FlexBox } from '@ardoq/layout';
import { colors } from '@ardoq/design-tokens';
import { getViewStyleGraphicName } from 'views/viewStyleGraphics';

// Every view-style graphic draws its artwork inside a 136x76 region centred in
// its 200x100 viewBox, the rest being empty margin baked into the asset.
// 'viewBox' shows the asset as authored. 'artwork' renders it at 0.8 and clips
// to the artwork plus a 6 unit margin, trimming most of that whitespace.
export type ViewStyleThumbnailFit = 'viewBox' | 'artwork' | 'dropdown';

type Size = { width: number; height: number };

const FITS: Record<ViewStyleThumbnailFit, { container: Size; graphic: Size }> =
  {
    viewBox: {
      container: { width: 119, height: 59 },
      graphic: { width: 119, height: 59 },
    },
    artwork: {
      container: { width: 118, height: 70 },
      graphic: { width: 160, height: 80 },
    },
    dropdown: {
      container: { width: 80, height: 56 },
      graphic: { width: 80, height: 56 },
    },
  };

const ThumbnailContainer = styled(FlexBox).attrs({
  align: 'center',
  justify: 'center',
  overflow: 'hidden',
  flexShrink: 0,
  borderWidth: 1,
  borderColor: 'borderSubtle',
  borderRadius: 'small',
  backgroundColor: 'surfaceDefaultSubtle',
})<{ $size: Size }>`
  width: ${({ $size }) => $size.width}px;
  height: ${({ $size }) => $size.height}px;
`;

type ViewStyleThumbnailProps = {
  viewId: ViewIds;
  fit: ViewStyleThumbnailFit;
};

const ViewStyleThumbnail = ({ viewId, fit }: ViewStyleThumbnailProps) => {
  const svgImageName = getViewStyleGraphicName(viewId);
  const { container, graphic } = FITS[fit];
  return (
    <ThumbnailContainer $size={container} aria-hidden>
      {svgImageName ? (
        <SvgImage
          svgImageName={svgImageName}
          width={graphic.width}
          height={graphic.height}
        />
      ) : (
        <Icon
          iconName={IconName.VIEWPOINT}
          iconSize={IconSize.MEDIUM}
          color={colors.icon}
        />
      )}
    </ThumbnailContainer>
  );
};

export { ViewStyleThumbnail };
